import { inject, Injectable, signal } from '@angular/core';
import { UserService, UserType } from '../user-management/user.service';
import { RestApiV2Service } from '../http/rest-api-v2.service';
import { take } from 'rxjs';
import { Pacient } from '../../../shared/models/users/pacient.model';
import { Treatment } from '../../../shared/models/treatments/treatment.model';
import { HighDemandMolecule } from '../../../shared/models/treatments/high-demand-molecule.model';
import { NurseUserInfo, DoctorUserInfo } from '../../../shared/models/users/user.model';
import { DashboardInteraction, DashboardInteractionItem, InteractionType } from '../../../shared/models/treatments/dashboardInteraction.model';
import { Latency } from '../../../shared/models/treatments/latency.model';
import { Effect } from '../../../shared/models/treatments/effects.model';

export interface PharmaceuticalDashboardStats {
  interactions: {
    totalCount: number;
    complianceRate: number;
    alerts: { read: number; unread: number };
    messages: { read: number; unread: number };
    prescriptions: { read: number; unread: number };
  };
  latency: {
    byMolecule: { molecule: string; averageLatency: number }[];
    averageDays: number;
  };
  treatmentsByCity: { city: string; count: number }[];
  moleculesByEPS: {
    eps: string;
    molecules: { molecule: string; count: number }[];
  }[];
}

export interface PharmaceuticalProfessionalNurse extends NurseUserInfo {
  interactions: DashboardInteraction;
  latency: Latency[];
}

export interface PharmaceuticalProfessionalDoctor extends DoctorUserInfo {
  interactions: DashboardInteraction;
  latency: Latency[];
}

export type PharmaceuticalProfessional = PharmaceuticalProfessionalNurse | PharmaceuticalProfessionalDoctor;

export interface PharmaceuticalData {
  molecules: HighDemandMolecule[];
  pacients: {pacient: Pacient, treatments: Treatment[] }[];
  professionals: {professional: PharmaceuticalProfessional, pacients: {pacient: Pacient, treatments: Treatment[] }[]}[];
}

@Injectable({
  providedIn: 'root',
})
export class PharmaceuticalService {
  private readonly userService = inject(UserService);
  private readonly restApiV2 = inject(RestApiV2Service);

  readonly data = signal<PharmaceuticalData>({ molecules: [], pacients: [], professionals: [] });

  getMyData() {
    const user = this.userService.userData();
    const response = this.restApiV2.get<{data: { data: PharmaceuticalData } }>('pharmaceutical/get-my-data', { admin_id: user!.id });

    response.request$.pipe(take(1)).subscribe((response) => {
      this.processData(response.data.data);
    });
  }

  processData(data: PharmaceuticalData) {
    console.log(data);
    this.data.set(data);
  }

  processPharmaceuticalDashboardStats(data: PharmaceuticalData): PharmaceuticalDashboardStats {
    // Procesar interacciones - sumar los valores de cada interacción de todos los profesionales
    let readAlerts = 0;
    let unreadAlerts = 0;
    let readMessages = 0;
    let unreadMessages = 0;
    let readPrescriptions = 0;
    let unreadPrescriptions = 0;

    data.professionals.forEach(prof => {
      readAlerts += prof.professional.interactions.read_alerts?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;
      unreadAlerts += prof.professional.interactions.unread_alerts?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;
      readMessages += prof.professional.interactions.read_messages?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;
      unreadMessages += prof.professional.interactions.unread_messages?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;
      readPrescriptions += prof.professional.interactions.read_prescriptions?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;
      unreadPrescriptions += prof.professional.interactions.unread_prescriptions?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;
    });

    const totalInteractions = readAlerts + unreadAlerts + readMessages + unreadMessages + readPrescriptions + unreadPrescriptions;
    const totalRead = readAlerts + readMessages + readPrescriptions;
    const complianceRate = totalInteractions > 0 ? (totalRead / totalInteractions) * 100 : 0;

    // Procesar latencia por molécula - de todos los profesionales
    const latencyByMolecule = new Map<string, { totalDays: number; count: number }>();
    let totalLatencyDays = 0;
    let totalLatencyCount = 0;

    data.professionals.forEach(prof => {
      prof.professional.latency.forEach(latency => {
        if (latency.prescription_date && latency.dispensing_date) {
          const prescriptionDate = new Date(latency.prescription_date);
          const dispensingDate = new Date(latency.dispensing_date);
          const daysDiff = Math.ceil((dispensingDate.getTime() - prescriptionDate.getTime()) / (1000 * 60 * 60 * 24));
          
          // Asegurar que siempre haya al menos 1 día de latencia
          const latencyDays = Math.max(1, daysDiff);
          
          totalLatencyDays += latencyDays;
          totalLatencyCount++;

          const molecule = latency.molecule;
          if (!latencyByMolecule.has(molecule)) {
            latencyByMolecule.set(molecule, { totalDays: 0, count: 0 });
          }
          const moleculeData = latencyByMolecule.get(molecule)!;
          moleculeData.totalDays += latencyDays;
          moleculeData.count++;
        }
      });
    });

    const averageDays = totalLatencyCount > 0 ? Math.round((totalLatencyDays / totalLatencyCount) * 100) / 100 : 0;

    const latencyByMoleculeArray = Array.from(latencyByMolecule.entries())
      .map(([molecule, data]) => ({
        molecule,
        averageLatency: data.count > 0 ? Math.round((data.totalDays / data.count) * 100) / 100 : 0,
      }))
      .sort((a, b) => b.averageLatency - a.averageLatency);

    // Procesar tratamientos por ciudad
    const treatmentsByCity = new Map<string, number>();
    data.pacients.forEach(p => {
      const city = p.pacient.city || 'Sin ciudad';
      const treatmentCount = p.treatments.length;
      treatmentsByCity.set(city, (treatmentsByCity.get(city) || 0) + treatmentCount);
    });

    const treatmentsByCityArray = Array.from(treatmentsByCity.entries())
      .map(([city, count]) => ({ city, count }))
      .sort((a, b) => b.count - a.count);

    // Procesar distribución de moléculas por EPS
    // Estructura: EPS -> Molécula -> Cantidad
    const moleculesByEPS = new Map<string, Map<string, number>>();
    
    data.pacients.forEach(p => {
      const epsName = p.pacient.insurer?.eps_name || 'Sin EPS';
      
      if (!moleculesByEPS.has(epsName)) {
        moleculesByEPS.set(epsName, new Map<string, number>());
      }
      
      const epsMolecules = moleculesByEPS.get(epsName)!;
      
      p.treatments.forEach(t => {
        const molecule = t.medicine?.molecule?.molecule || 'Sin molécula';
        epsMolecules.set(molecule, (epsMolecules.get(molecule) || 0) + 1);
      });
    });

    // Convertir a formato para stacked bar chart
    // Necesitamos obtener todas las moléculas únicas y todas las EPS
    const allMolecules = new Set<string>();
    moleculesByEPS.forEach(epsMolecules => {
      epsMolecules.forEach((_, molecule) => {
        allMolecules.add(molecule);
      });
    });

    const epsNames = Array.from(moleculesByEPS.keys()).sort();
    const moleculesByEPSArray = epsNames.map(epsName => {
      const epsMolecules = moleculesByEPS.get(epsName)!;
      return {
        eps: epsName,
        molecules: Array.from(allMolecules).map(molecule => ({
          molecule,
          count: epsMolecules.get(molecule) || 0,
        })),
      };
    });

    return {
      interactions: {
        totalCount: totalInteractions,
        complianceRate: Math.round(complianceRate * 100) / 100,
        alerts: { read: readAlerts, unread: unreadAlerts },
        messages: { read: readMessages, unread: unreadMessages },
        prescriptions: { read: readPrescriptions, unread: unreadPrescriptions },
      },
      latency: {
        byMolecule: latencyByMoleculeArray,
        averageDays: averageDays,
      },
      treatmentsByCity: treatmentsByCityArray,
      moleculesByEPS: moleculesByEPSArray,
    };
  }

  managePharmaceuticalTreatment(treatment: Treatment, socketType: 'NEW_TREATMENT' | 'ALTER_TREATMENT' | 'REMOVE_TREATMENT') {
    this.data.update(currentData => {
      const pacientId = treatment.pacient_id;
      const pacientIndex = currentData.pacients.findIndex(p => p.pacient.id === pacientId);
      
      // REMOVE_TREATMENT y ALTER_TREATMENT se comportan igual: actualizan el tratamiento
      if (socketType === 'ALTER_TREATMENT' || socketType === 'REMOVE_TREATMENT') {
        if (pacientIndex === -1) {
          return this.updateTreatmentInProfessionals(currentData, treatment);
        }
        
        const pacient = currentData.pacients[pacientIndex];
        const treatmentIndex = pacient.treatments.findIndex(t => t.id === treatment.id);
        
        if (treatmentIndex === -1) {
          return this.updateTreatmentInProfessionals(currentData, treatment);
        }
        
        const updatedPacients = currentData.pacients.map((p, idx) => 
          idx === pacientIndex 
            ? { ...p, treatments: p.treatments.map((t, tIdx) => tIdx === treatmentIndex ? treatment : t) }
            : p
        );
        
        return {
          ...currentData,
          pacients: updatedPacients,
          professionals: this.updateTreatmentInProfessionalsArray(currentData.professionals, treatment)
        };
      }
      
      // NEW_TREATMENT
      if (pacientIndex !== -1) {
        const pacient = currentData.pacients[pacientIndex];
        const treatmentIndex = pacient.treatments.findIndex(t => t.id === treatment.id);
        
        const updatedPacients = currentData.pacients.map((p, idx) => 
          idx === pacientIndex
            ? { ...p, treatments: treatmentIndex !== -1 
                ? p.treatments.map((t, tIdx) => tIdx === treatmentIndex ? treatment : t)
                : [...p.treatments, treatment]
              }
            : p
        );
        
        return {
          ...currentData,
          pacients: updatedPacients,
          professionals: this.updateTreatmentInProfessionalsArray(currentData.professionals, treatment)
        };
      }
      
      if (treatment.pacient) {
        return {
          ...currentData,
          pacients: [...currentData.pacients, { pacient: treatment.pacient, treatments: [treatment] }],
          professionals: this.updateTreatmentInProfessionalsArray(currentData.professionals, treatment)
        };
      }
      
      return this.updateTreatmentInProfessionals(currentData, treatment);
    });
  }

  private updateTreatmentInProfessionals(currentData: PharmaceuticalData, treatment: Treatment): PharmaceuticalData {
    return {
      ...currentData,
      professionals: this.updateTreatmentInProfessionalsArray(currentData.professionals, treatment)
    };
  }

  private updateTreatmentInProfessionalsArray(
    professionals: {professional: PharmaceuticalProfessional, pacients: {pacient: Pacient, treatments: Treatment[] }[]}[],
    treatment: Treatment
  ): {professional: PharmaceuticalProfessional, pacients: {pacient: Pacient, treatments: Treatment[] }[]}[] {
    return professionals.map(prof => {
      const pacientIndex = prof.pacients.findIndex(p => p.pacient.id === treatment.pacient_id);
      
      if (pacientIndex === -1) {
        if (treatment.pacient) {
          return {
            ...prof,
            pacients: [...prof.pacients, { pacient: treatment.pacient, treatments: [treatment] }]
          };
        }
        return prof;
      }
      
      const pacient = prof.pacients[pacientIndex];
      const treatmentIndex = pacient.treatments.findIndex(t => t.id === treatment.id);
      
      return {
        ...prof,
        pacients: prof.pacients.map((p, idx) => 
          idx === pacientIndex
            ? { ...p, treatments: treatmentIndex !== -1 
                ? p.treatments.map((t, tIdx) => tIdx === treatmentIndex ? treatment : t)
                : [...p.treatments, treatment]
              }
            : p
        )
      };
    });
  }

  addEffectsToTreatments(effects: Effect[]) {
    this.data.update(currentData => {
      return {
        ...currentData,
        pacients: this.addEffectsToPacients(currentData.pacients, effects),
        professionals: currentData.professionals.map(prof => ({
          ...prof,
          pacients: this.addEffectsToPacients(prof.pacients, effects)
        }))
      };
    });
  }

  private addEffectsToPacients(
    pacients: {pacient: Pacient, treatments: Treatment[] }[],
    effects: Effect[]
  ): {pacient: Pacient, treatments: Treatment[] }[] {
    return pacients.map(pacient => ({
      ...pacient,
      treatments: pacient.treatments.map(treatment => {
        const treatmentEffects = effects.filter(e => e.treatment_id === treatment.id);
        if (treatmentEffects.length === 0) return treatment;
        
        const existingEffectIds = new Set(treatment.effects.map(e => e.id));
        const newEffects = treatmentEffects.filter(e => !existingEffectIds.has(e.id));
        
        return {
          ...treatment,
          effects: [...treatment.effects, ...newEffects]
        };
      })
    }));
  }

  updateDashboardInteraction(interaction: DashboardInteractionItem) {
    this.data.update(currentData => ({
      ...currentData,
      professionals: currentData.professionals.map(prof => 
        prof.professional.id === interaction.professional_id
          ? {
              ...prof,
              professional: {
                ...prof.professional,
                interactions: this.addOrUpdateInteraction(prof.professional.interactions, interaction)
              }
            }
          : prof
      )
    }));
  }

  private addOrUpdateInteraction(interactions: DashboardInteraction, item: DashboardInteractionItem): DashboardInteraction {
    const arrayKey = this.getInteractionArrayKey(item.interaction_type);
    if (!arrayKey) return interactions;

    const currentArray = interactions[arrayKey] || [];
    const existingIndex = currentArray.findIndex(i => i.id === item.id);
    
    const updatedArray = existingIndex !== -1
      ? currentArray.map((i, idx) => idx === existingIndex ? item : i)
      : [...currentArray, item];

    return {
      ...interactions,
      [arrayKey]: updatedArray
    };
  }

  private getInteractionArrayKey(type: InteractionType): keyof DashboardInteraction | null {
    switch (type) {
      case InteractionType.READ_ALERTS: return 'read_alerts';
      case InteractionType.UNREAD_ALERTS: return 'unread_alerts';
      case InteractionType.READ_MESSAGES: return 'read_messages';
      case InteractionType.UNREAD_MESSAGES: return 'unread_messages';
      case InteractionType.READ_PRESCRIPTIONS: return 'read_prescriptions';
      case InteractionType.UNREAD_PRESCRIPTIONS: return 'unread_prescriptions';
      default: return null;
    }
  }

  addProfessional(professional: DoctorUserInfo | NurseUserInfo) {
    this.data.update(currentData => {
      const professionalExists = currentData.professionals.some(
        prof => prof.professional.id === professional.id
      );

      if (professionalExists) return currentData;

      const defaultInteractions: DashboardInteraction = {
        read_alerts: [],
        unread_alerts: [],
        read_prescriptions: [],
        unread_prescriptions: [],
        read_messages: [],
        unread_messages: []
      };

      if (professional.user_type === UserType.DOCTOR) {
        const pharmaceuticalProfessional: PharmaceuticalProfessionalDoctor = {
          ...professional as DoctorUserInfo,
          interactions: defaultInteractions,
          latency: []
        };

        return {
          ...currentData,
          professionals: [
            ...currentData.professionals,
            {
              professional: pharmaceuticalProfessional,
              pacients: []
            }
          ]
        };
      } else if (professional.user_type === UserType.NURSE) {
        const pharmaceuticalProfessional: PharmaceuticalProfessionalNurse = {
          ...professional as NurseUserInfo,
          interactions: defaultInteractions,
          latency: []
        };

        return {
          ...currentData,
          professionals: [
            ...currentData.professionals,
            {
              professional: pharmaceuticalProfessional,
              pacients: []
            }
          ]
        };
      }

      return currentData;
    });
  }

  addOrUpdateMolecule(molecule: HighDemandMolecule) {
    this.data.update(currentData => {
      const moleculeIndex = currentData.molecules.findIndex(m => m.id === molecule.id);
      
      if (moleculeIndex !== -1) {
        return {
          ...currentData,
          molecules: currentData.molecules.map((m, idx) => idx === moleculeIndex ? molecule : m)
        };
      }
      
      return {
        ...currentData,
        molecules: [...currentData.molecules, molecule]
      };
    });
  }

  addOrUpdateLatency(latency: Latency) {
    this.data.update(currentData => ({
      ...currentData,
      professionals: currentData.professionals.map(prof => 
        prof.professional.id === latency.professional_id
          ? {
              ...prof,
              professional: {
                ...prof.professional,
                latency: this.addOrUpdateLatencyInArray(prof.professional.latency, latency)
              }
            }
          : prof
      )
    }));
  }

  private addOrUpdateLatencyInArray(latencyArray: Latency[], latency: Latency): Latency[] {
    const latencyIndex = latencyArray.findIndex(l => l.id === latency.id);
    
    if (latencyIndex !== -1) {
      return latencyArray.map((l, idx) => idx === latencyIndex ? latency : l);
    }
    
    return [...latencyArray, latency];
  }
}